import { Popover } from "@mantine/core";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";

import Button from "~/components/ui/button/Button";

import {
  DateRangeSafe,
  isDateRangeSafe,
} from "../DashboardView/utils/useDateRangeFilter";
import styles from "./DateRangePicker.module.scss";
import { QUICK_DIAL_OPTIONS } from "./constants";

export const DateRangePicker = ({
  value,
  onChange,
  disabled,
}: {
  value: DateRangeSafe;
  onChange: (value: DateRangeSafe) => void;
  disabled?: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selection, setSelection] = useState<DateRange | undefined>(value);
  const [preview, setPreview] = useState<DateRangeSafe | undefined>(undefined);

  const { isQuickDialHighlighted, onQuickDialUsed } =
    useQuickDialHighlightState(value);

  // Update internal state when external value changes
  useEffect(() => {
    setSelection(value);
  }, [value]);

  const handlePopoverChange = (opened: boolean, newRange?: DateRangeSafe) => {
    setIsPopoverOpen(opened);

    const updatedRange = newRange ?? selection;

    if (!opened && isDateRangeSafe(updatedRange)) {
      // Only call onChange if the internal range is different from the current value
      if (!areDateRangesEqual(updatedRange, value)) {
        onChange(updatedRange);
      }
    }
  };

  const displayValue = preview ?? selection;

  const isApplyButtonDisabled = () => {
    if (!displayValue) {
      return true;
    }

    if (!isDateRangeSafe(displayValue)) {
      return true;
    }

    return areDateRangesEqual(displayValue, value);
  };

  const getApplyButtonLabel = () => {
    if (!displayValue) {
      return "Apply";
    }

    if (!isDateRangeSafe(displayValue)) {
      return "Apply";
    }

    if (areDateRangesEqual(displayValue, value)) {
      return "Apply";
    }

    return `Apply ${formatDateRange(displayValue)}`;
  };

  return (
    <Popover
      offset={1}
      position="bottom"
      opened={isPopoverOpen}
      onChange={handlePopoverChange}
      styles={{
        dropdown: {
          minWidth: "150px",
        },
      }}
    >
      <Popover.Target>
        <Button
          outlined
          onClick={() => setIsPopoverOpen((o) => !o)}
          disabled={disabled}
        >
          {formatDateRange(value)}
        </Button>
      </Popover.Target>
      <Popover.Dropdown className={styles.dropdown}>
        <div className={styles.panels}>
          <div className={styles.quickDialOptions}>
            {QUICK_DIAL_OPTIONS.map((option, idx) => {
              const isHighlighted = isQuickDialHighlighted(option.label);

              return (
                <Button
                  key={idx}
                  outlined={!isHighlighted}
                  onClick={() => {
                    onQuickDialUsed(option.label);
                    handlePopoverChange(false, option.value());
                  }}
                  onMouseEnter={() => setPreview(option.value())}
                  onMouseLeave={() => setPreview(undefined)}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
          <DayPicker
            mode="range"
            selected={displayValue}
            onSelect={(newRange) => {
              setSelection(newRange);
            }}
            numberOfMonths={2}
            defaultMonth={
              new Date(new Date().getFullYear(), new Date().getMonth() - 1)
            }
            disabled={disabled || { after: new Date() }}
            weekStartsOn={1}
          />
        </div>
        <div className={styles.actions}>
          <Button outlined onClick={() => handlePopoverChange(false, value)}>
            Cancel
          </Button>
          <Button
            disabled={isApplyButtonDisabled()}
            onClick={() => handlePopoverChange(false)}
          >
            {getApplyButtonLabel()}
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};

const formatDateRange = (range: DateRangeSafe) => {
  const fromYear = range.from.getFullYear();
  const toYear = range.to.getFullYear();

  // If same year, show abbreviated format
  if (fromYear === toYear) {
    return `${format(range.from, "MMM d")} - ${format(range.to, "MMM d, yyyy")}`;
  }

  // If different years, show full format
  return `${format(range.from, "MMM d, yyyy")} - ${format(range.to, "MMM d, yyyy")}`;
};

const useQuickDialHighlightState = (value: DateRangeSafe) => {
  const [lastUsedQuickDialLabel, setLastUsedQuickDialLabel] = useState<
    string | null
  >(null);

  const candidates = QUICK_DIAL_OPTIONS.filter((option) =>
    areDateRangesEqual(option.value(), value),
  );

  const highlightedQuickDial =
    candidates.find(
      (candidate) => candidate.label === lastUsedQuickDialLabel,
    ) ??
    candidates[0] ??
    null;

  const isQuickDialHighlighted = (label: string): boolean => {
    return highlightedQuickDial?.label === label;
  };

  const onQuickDialUsed = (label: string) => {
    setLastUsedQuickDialLabel(label);
  };

  return {
    isQuickDialHighlighted,
    onQuickDialUsed,
  };
};

const areDateRangesEqual = (range1: DateRangeSafe, range2: DateRangeSafe) => {
  return (
    range1.from.getTime() === range2.from.getTime() &&
    range1.to.getTime() === range2.to.getTime()
  );
};
